// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";

// CSS
import "./BuildingElementsEditAdd.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import {
	deleteAPICall,
	postAPICall,
	putAPICall,
} from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// MUI COMPONENTS
import {
	Alert,
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	TextField,
} from "@mui/material";

// MUI ICONS
import CloseIcon from "@mui/icons-material/Close";

// REACT COMPONENT
const BuildingElementsEditAdd = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// STATES
	const [loading, setLoading] = useState(false);
	const [alertOpen, setAlertOpen] = useState(false);
	const [alertMessage, setAlertMessage] = useState("");

	const [buildingType, setBuildingType] = useState("");

	// CONTEXT
	const employeeID = useContext(UserContext);

	// USE EFFECT
	useEffect(() => {
		if (props.open && props.editing && props.recordToEdit) {
			setBuildingType(props.recordToEdit.building_type);
		}
	}, [props.open, props.recordToEdit]);

	// INPUT HANDLERS
	const buildingTypeChanged = (event) => {
		setBuildingType(event.target.value);
	};

	// API FUNCTIONS
	const handleUpdateRecord = async () => {
		await putAPICall(instance, accounts[0], "/api/coreRepairScopes/update/buildingElements", {
			id: props.recordToEdit.id,
			building_type: buildingType,
			modifiedBy: employeeID,
		});
	};

	const handleAddRecord = async () => {
		await postAPICall(instance, accounts[0], "/api/coreRepairScopes/add/buildingElements", {
			building_type: buildingType,
			modifiedBy: employeeID,
		});
	};

	const handleDeleteRecord = async () => {
		await deleteAPICall(instance, accounts[0], "/api/coreRepairScopes/delete/buildingElements", {
			id: props.recordToEdit.id,
		});
	};

	// HELPER FUNCTIONS
	const handleSubmitClick = async () => {
		if (!validateInputFields()) return;
		setLoading(true);
		try {
			if (props.editing) {
				await handleUpdateRecord();
			} else {
				await handleAddRecord();
			}
		} catch (err) {
			console.error(err);
		}
		setAlertMessage("");
		setAlertOpen(false);
		setLoading(false);
		props.onClose(true);
	};

	const handleCancelClick = () => {
		setAlertMessage("");
		setAlertOpen(false);
		props.onClose(false);
	};

	const handleDeleteClick = async () => {
		setLoading(true);
		await handleDeleteRecord();
		setAlertMessage("");
		setAlertOpen(false);
		setLoading(false);
		props.onClose(true);
	};

	const resetInputFields = () => {
		setBuildingType("");
	};

	const validateInputFields = () => {
		if (buildingType === "") {
			setAlertMessage("Building type is required");
			setAlertOpen(true);
			return false;
		}
		return true;
	};

	// RENDER
	return (
		<Dialog
			id="BuildingElementsEditAddDialog"
			open={props.open}
			fullWidth
			maxWidth="sm"
			onClose={handleCancelClick}
			TransitionProps={{ onExited: resetInputFields }}
		>
			<DialogTitle id="BuildingElementsEditAddDialogTitle">
				{props.editing ? "Edit Building Element" : "Add Building Element"}
				{props.editing && (
					<Button
						color="error"
						variant="contained"
						size="small"
						onClick={handleDeleteClick}
					>
						Delete
					</Button>
				)}
			</DialogTitle>

			<DialogContent id="BuildingElementsEditAddDialogContent">
				{loading ? (
					<div className="editBuildingElementsLoadingContainer">
						<CircularProgress />
					</div>
				) : (
					<>
						{alertOpen && (
							<Alert
								severity="warning"
								sx={{ mb: 2 }}
								action={
									<IconButton
										aria-label="close"
										color="inherit"
										size="small"
										onClick={() => setAlertOpen(false)}
									>
										<CloseIcon />
									</IconButton>
								}
							>
								{alertMessage}
							</Alert>
						)}

						<TextField
							id="buildingType"
							label="Building Type"
							variant="outlined"
							value={buildingType}
							onChange={buildingTypeChanged}
							fullWidth
						/>

						<div className="editBuildingElementsButtons" style={{ marginTop: "1em" }}>
							<Button
								variant="outlined"
								color="error"
								onClick={handleCancelClick}
							>
								Cancel
							</Button>
							<Button
								variant="contained"
								color="primary"
								onClick={handleSubmitClick}
							>
								Submit
							</Button>
						</div>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
};

// EXPORT
export default BuildingElementsEditAdd;
